import { create } from 'zustand'

// ============================================================
// Istanza: stesso codice, stessa build, ma un'installazione può essere
// una "seconda istanza" per un'altra azienda (es. PROXIMA) che vede solo
// un sottoinsieme di moduli (tipicamente Contabilità ed Email). Lo stato
// arriva da GET /api/v1/instance (pubblica, letta all'avvio dell'app,
// anche prima del login) — vedi backend/src/config/instance.ts.
//
// Se la chiamata fallisce si resta sul default 'all': comportamento
// completo, esattamente come oggi per E-quipe.
//
// fetchInstance() usa fetch() diretto (non apiClient/axiosInstance): è una
// route pubblica, senza token, e va risolta PRIMA del render (vedi
// main.tsx) — niente interceptor di refresh token di mezzo, e un timeout
// breve così un backend irraggiungibile non blocca l'avvio dell'app.
// ============================================================

export interface InstanceInfo {
  name: string
  modules: 'all' | string[]
}

// Mappa modulo → prima route di quel modulo (per sidebar e redirect)
const MODULE_HOME: Record<string, string> = {
  contabilita: '/accounting',
  email: '/email',
}

const DEFAULT_INSTANCE: InstanceInfo = { name: 'E-QUIPE ERP', modules: 'all' }
const FETCH_TIMEOUT_MS = 5000

interface InstanceState {
  instance: InstanceInfo
  loaded: boolean
  fetchInstance: () => Promise<void>
}

export const useInstanceStore = create<InstanceState>((set) => ({
  instance: DEFAULT_INSTANCE,
  loaded: false,

  fetchInstance: async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

      const res = await fetch(`${baseUrl}/instance`, { signal: controller.signal })
      clearTimeout(timeoutId)

      if (!res.ok) throw new Error(`GET /instance → ${res.status}`)

      const body = await res.json() as { success?: boolean; data?: InstanceInfo } | InstanceInfo
      const data = (body as { data?: InstanceInfo }).data ?? (body as InstanceInfo)

      if (!data?.name || (data.modules !== 'all' && !Array.isArray(data.modules))) {
        throw new Error('Risposta /instance malformata')
      }

      set({ instance: data, loaded: true })
    } catch {
      // Chiamata fallita (o in timeout) → comportamento completo (come oggi)
      set({ instance: DEFAULT_INSTANCE, loaded: true })
    }
  },
}))

// Selettori/helper puri, derivati dallo stato corrente dello store
export const isInstanceComplete = (instance: InstanceInfo): boolean => instance.modules === 'all'

export const isModuleActive = (instance: InstanceInfo, modulo: string): boolean =>
  instance.modules === 'all' || instance.modules.includes(modulo)

// Prima route disponibile per un'istanza non completa: il primo modulo attivo
// riconosciuto nell'elenco (non necessariamente il primo in assoluto, se quello
// è scritto male o non mappato). Il fallback non è mai "/": quella route è
// proprio la Dashboard che l'istanza non completa non può mostrare, e
// rimandarci creerebbe un redirect su se stessa.
export const firstModulePath = (instance: InstanceInfo): string => {
  if (instance.modules === 'all') return '/'
  for (const modulo of instance.modules) {
    const path = MODULE_HOME[modulo]
    if (path) return path
  }
  return '/accounting'
}
